/**
 * AlertsTab Component
 *
 * Manages alert groups - flexible notification configurations where each group has:
 * - Custom name (e.g., "Management Team", "Field Technicians")
 * - Multiple email recipients
 * - Selected alert types (shown as pills)
 *
 * Features:
 * - Table showing all alert groups
 * - Create/Edit modal for configuring groups
 * - Add/remove email recipients
 * - Toggle alert types per group
 * - Delete alert groups
 */

import { useState, useEffect } from 'react';
import { createPortal } from 'react-dom';
import { supabase } from '../../lib/supabase';
import { useAuth } from '../../contexts/AuthContext';

// Available alert types
const ALERT_TYPES = [
  { id: 'new_assessment', label: 'New Assessment', icon: '📋', description: 'When a new HVAC assessment is created' },
  { id: 'team_member_added', label: 'Team Member Added', icon: '👥', description: 'When a new team member joins' },
  { id: 'subscription_expiring', label: 'Subscription Expiring', icon: '⏰', description: 'Three days before subscription renewal' },
  { id: 'usage_threshold', label: 'Usage Threshold', icon: '📊', description: 'When assessment usage reaches 80% of monthly limit' },
  { id: 'payment_failed', label: 'Payment Failed', icon: '💳', description: 'When a payment attempt fails' },
  { id: 'new_customer', label: 'New Customer', icon: '✨', description: 'When a new customer is added' }
];

export default function AlertsTab() {
  const { contractor } = useAuth();
  const [alertGroups, setAlertGroups] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(false);

  // Modal state
  const [showModal, setShowModal] = useState(false);
  const [modalMode, setModalMode] = useState('add'); // 'add' or 'edit'
  const [editingGroup, setEditingGroup] = useState(null);

  // Form state
  const [groupName, setGroupName] = useState('');
  const [recipients, setRecipients] = useState([]); // Array of email strings
  const [newRecipientEmail, setNewRecipientEmail] = useState('');
  const [selectedAlerts, setSelectedAlerts] = useState(new Set()); // Set of alert IDs

  // Load alert groups
  useEffect(() => {
    if (contractor?.id) {
      loadAlertGroups();
    }
  }, [contractor?.id]);

  async function loadAlertGroups() {
    try {
      setLoading(true);
      const { data, error } = await supabase
        .from('alert_groups')
        .select('*')
        .eq('contractor_id', contractor.id)
        .order('created_at', { ascending: false });

      if (error) throw error;

      setAlertGroups(data || []);
    } catch (err) {
      console.error('Error loading alert groups:', err);
      setError('Failed to load alert groups');
    } finally {
      setLoading(false);
    }
  }

  // Open add modal
  function handleAdd() {
    setModalMode('add');
    setGroupName('');
    setRecipients([]);
    setNewRecipientEmail('');
    setSelectedAlerts(new Set());
    setEditingGroup(null);
    setShowModal(true);
    setError(null);
  }

  // Open edit modal
  function handleEdit(group) {
    setModalMode('edit');
    setGroupName(group.group_name);
    setRecipients(group.email_recipients || []);
    setNewRecipientEmail('');
    setSelectedAlerts(new Set(group.alert_types || []));
    setEditingGroup(group);
    setShowModal(true);
    setError(null);
  }

  // Close modal
  function handleCloseModal() {
    setShowModal(false);
    setGroupName('');
    setRecipients([]);
    setNewRecipientEmail('');
    setSelectedAlerts(new Set());
    setEditingGroup(null);
    setError(null);
  }

  // Add recipient email
  function handleAddRecipient() {
    const email = newRecipientEmail.trim().toLowerCase();

    // Basic email validation
    if (!email) return;
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) {
      setError('Please enter a valid email address');
      return;
    }
    if (recipients.includes(email)) {
      setError('This email is already in the list');
      return;
    }

    setRecipients([...recipients, email]);
    setNewRecipientEmail('');
    setError(null);
  }

  // Remove recipient email
  function handleRemoveRecipient(email) {
    setRecipients(recipients.filter(r => r !== email));
  }

  // Toggle alert type
  function toggleAlertType(alertId) {
    const newSelected = new Set(selectedAlerts);
    if (newSelected.has(alertId)) {
      newSelected.delete(alertId);
    } else {
      newSelected.add(alertId);
    }
    setSelectedAlerts(newSelected);
  }

  // Save alert group
  async function handleSave(e) {
    e.preventDefault();
    e.stopPropagation();

    setSaving(true);
    setError(null);
    setSuccess(false);

    try {
      // Validation
      if (!groupName.trim()) {
        throw new Error('Group name is required');
      }
      if (recipients.length === 0) {
        throw new Error('At least one email recipient is required');
      }
      if (selectedAlerts.size === 0) {
        throw new Error('At least one alert type must be selected');
      }

      const groupData = {
        contractor_id: contractor.id,
        group_name: groupName.trim(),
        email_recipients: recipients,
        alert_types: Array.from(selectedAlerts),
        is_enabled: true
      };

      if (modalMode === 'edit') {
        // Update existing group
        const { error: updateError } = await supabase
          .from('alert_groups')
          .update(groupData)
          .eq('id', editingGroup.id);

        if (updateError) throw updateError;
      } else {
        // Insert new group
        const { error: insertError } = await supabase
          .from('alert_groups')
          .insert(groupData);

        if (insertError) throw insertError;
      }

      // Reload groups
      await loadAlertGroups();

      setSuccess(true);
      setTimeout(() => setSuccess(false), 3000);

      handleCloseModal();
    } catch (err) {
      console.error('Error saving alert group:', err);
      setError(err.message || 'Failed to save alert group');
    } finally {
      setSaving(false);
    }
  }

  // Delete alert group
  async function handleDelete(groupId) {
    if (!confirm('Are you sure you want to delete this alert group? This cannot be undone.')) {
      return;
    }

    try {
      setSaving(true);
      setError(null);

      const { error: deleteError } = await supabase
        .from('alert_groups')
        .delete()
        .eq('id', groupId);

      if (deleteError) throw deleteError;

      await loadAlertGroups();

      setSuccess(true);
      setTimeout(() => setSuccess(false), 3000);
    } catch (err) {
      console.error('Error deleting alert group:', err);
      setError(err.message || 'Failed to delete alert group');
    } finally {
      setSaving(false);
    }
  }

  // Toggle group enabled status
  async function handleToggleEnabled(groupId, currentStatus) {
    try {
      setSaving(true);

      const { error: updateError } = await supabase
        .from('alert_groups')
        .update({ is_enabled: !currentStatus })
        .eq('id', groupId);

      if (updateError) throw updateError;

      await loadAlertGroups();
    } catch (err) {
      console.error('Error toggling alert group:', err);
      setError(err.message || 'Failed to update alert group');
    } finally {
      setSaving(false);
    }
  }

  if (loading) {
    return (
      <div className="card">
        <div style={{ textAlign: 'center', padding: '40px 20px' }}>
          <div style={{ fontSize: '16px', color: '#64748b' }}>Loading alert groups...</div>
        </div>
      </div>
    );
  }

  return (
    <div>
      {/* Success Message */}
      {success && (
        <div style={{
          padding: '15px',
          background: '#dcfce7',
          border: '2px solid #22c55e',
          borderRadius: '8px',
          color: '#14532d',
          marginBottom: '20px',
          display: 'flex',
          alignItems: 'center',
          gap: '10px'
        }}>
          <span>✅</span>
          <strong>Alert group saved successfully!</strong>
        </div>
      )}

      {/* Error Message */}
      {error && (
        <div style={{
          padding: '15px',
          background: '#fee2e2',
          border: '2px solid #ef4444',
          borderRadius: '8px',
          color: '#dc2626',
          marginBottom: '20px'
        }}>
          <strong>Error:</strong> {error}
        </div>
      )}

      {/* Info Card */}
      <div style={{
        padding: '15px',
        background: '#f0f9ff',
        borderRadius: '8px',
        marginBottom: '24px',
        fontSize: '14px',
        color: '#0c4a6e',
        lineHeight: '1.6'
      }}>
        <strong>💡 About Alert Groups:</strong> Create flexible notification groups with custom names, multiple recipients, and specific alert types. For example, create a "Management Team" group that receives subscription and payment alerts, or a "Field Technicians" group that gets notified about new assessments.
      </div>

      {/* Alert Groups Section */}
      <div className="card">
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px' }}>
          <h2 style={{ fontSize: '20px', margin: 0 }}>🔔 Alert Groups</h2>
          <button
            onClick={handleAdd}
            className="btn btn-primary"
            disabled={saving}
          >
            + Create New Alert Group
          </button>
        </div>

        {alertGroups.length === 0 ? (
          <div style={{
            padding: '40px',
            textAlign: 'center',
            background: '#f8fafc',
            borderRadius: '8px',
            color: '#64748b'
          }}>
            <div style={{ fontSize: '40px', marginBottom: '15px' }}>🔔</div>
            <div style={{ fontSize: '18px', fontWeight: '600', marginBottom: '8px' }}>No alert groups configured</div>
            <div style={{ fontSize: '14px', marginBottom: '20px' }}>Create your first alert group to start receiving notifications</div>
            <button
              onClick={handleAdd}
              className="btn btn-primary"
              disabled={saving}
            >
              + Create Your First Alert Group
            </button>
          </div>
        ) : (
          <div style={{ overflowX: 'auto' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse' }}>
              <thead>
                <tr style={{ borderBottom: '2px solid #e0e6ed' }}>
                  <th style={{ padding: '12px', textAlign: 'left', fontSize: '13px', fontWeight: '600', color: '#64748b' }}>
                    Status
                  </th>
                  <th style={{ padding: '12px', textAlign: 'left', fontSize: '13px', fontWeight: '600', color: '#64748b' }}>
                    Group Name
                  </th>
                  <th style={{ padding: '12px', textAlign: 'left', fontSize: '13px', fontWeight: '600', color: '#64748b' }}>
                    Recipients
                  </th>
                  <th style={{ padding: '12px', textAlign: 'left', fontSize: '13px', fontWeight: '600', color: '#64748b' }}>
                    Alert Types
                  </th>
                  <th style={{ padding: '12px', textAlign: 'right', fontSize: '13px', fontWeight: '600', color: '#64748b' }}>
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody>
                {alertGroups.map(group => (
                  <tr key={group.id} style={{ borderBottom: '1px solid #e0e6ed' }}>
                    <td style={{ padding: '16px' }}>
                      <label style={{ display: 'flex', alignItems: 'center', cursor: 'pointer' }}>
                        <input
                          type="checkbox"
                          checked={group.is_enabled}
                          onChange={() => handleToggleEnabled(group.id, group.is_enabled)}
                          disabled={saving}
                          style={{ marginRight: '8px' }}
                        />
                        <span style={{ fontSize: '13px', color: '#64748b' }}>
                          {group.is_enabled ? 'Active' : 'Disabled'}
                        </span>
                      </label>
                    </td>
                    <td style={{ padding: '16px' }}>
                      <div style={{ fontSize: '15px', fontWeight: '600', color: '#2c3e50' }}>
                        {group.group_name}
                      </div>
                    </td>
                    <td style={{ padding: '16px' }}>
                      <div style={{ fontSize: '13px', color: '#64748b' }}>
                        {group.email_recipients?.length || 0} recipient{group.email_recipients?.length !== 1 ? 's' : ''}
                      </div>
                      <div style={{ fontSize: '11px', color: '#94a3b8', marginTop: '2px' }}>
                        {group.email_recipients?.slice(0, 2).join(', ')}
                        {group.email_recipients?.length > 2 && ` +${group.email_recipients.length - 2} more`}
                      </div>
                    </td>
                    <td style={{ padding: '16px' }}>
                      <div style={{ display: 'flex', flexWrap: 'wrap', gap: '4px' }}>
                        {group.alert_types?.map(alertId => {
                          const alertType = ALERT_TYPES.find(a => a.id === alertId);
                          return alertType ? (
                            <span
                              key={alertId}
                              style={{
                                padding: '3px 8px',
                                background: '#e0f2fe',
                                border: '1px solid #0ea5e9',
                                borderRadius: '12px',
                                fontSize: '11px',
                                color: '#075985',
                                fontWeight: '500'
                              }}
                            >
                              {alertType.icon} {alertType.label}
                            </span>
                          ) : null;
                        })}
                      </div>
                    </td>
                    <td style={{ padding: '16px', textAlign: 'right' }}>
                      <div style={{ display: 'flex', gap: '8px', justifyContent: 'flex-end' }}>
                        <button
                          onClick={() => handleEdit(group)}
                          className="btn btn-secondary"
                          style={{ fontSize: '13px', padding: '6px 12px' }}
                          disabled={saving}
                        >
                          Edit
                        </button>
                        <button
                          onClick={() => handleDelete(group.id)}
                          className="btn"
                          style={{
                            fontSize: '13px',
                            padding: '6px 12px',
                            background: '#fee2e2',
                            color: '#dc2626',
                            border: '2px solid #ef4444'
                          }}
                          disabled={saving}
                        >
                          Delete
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Add/Edit Modal */}
      {showModal && createPortal(
        <div
          style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            background: 'rgba(0, 0, 0, 0.5)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 1000,
            padding: '20px'
          }}
          onClick={handleCloseModal}
        >
          <div
            style={{
              background: 'white',
              borderRadius: '12px',
              padding: '30px',
              maxWidth: '700px',
              width: '100%',
              maxHeight: '90vh',
              overflowY: 'auto'
            }}
            onClick={(e) => e.stopPropagation()}
          >
            <h3 style={{ fontSize: '20px', marginBottom: '20px' }}>
              {modalMode === 'edit' ? 'Edit Alert Group' : 'Create New Alert Group'}
            </h3>

            <form onSubmit={handleSave}>
              {/* Group Name */}
              <div style={{ marginBottom: '20px' }}>
                <label style={{ display: 'block', marginBottom: '8px', fontWeight: '600' }}>
                  Group Name <span style={{ color: '#ef4444' }}>*</span>
                </label>
                <input
                  type="text"
                  className="input"
                  value={groupName}
                  onChange={(e) => setGroupName(e.target.value)}
                  placeholder="e.g., Management Team, Field Technicians, Accounting"
                  required
                  autoFocus
                />
              </div>

              {/* Email Recipients */}
              <div style={{ marginBottom: '20px' }}>
                <label style={{ display: 'block', marginBottom: '8px', fontWeight: '600' }}>
                  Email Recipients <span style={{ color: '#ef4444' }}>*</span>
                </label>

                {/* Recipient List */}
                {recipients.length > 0 && (
                  <div style={{
                    marginBottom: '12px',
                    padding: '12px',
                    background: '#f8fafc',
                    borderRadius: '8px',
                    border: '2px solid #e0e6ed'
                  }}>
                    {recipients.map(email => (
                      <div
                        key={email}
                        style={{
                          display: 'flex',
                          justifyContent: 'space-between',
                          alignItems: 'center',
                          padding: '8px 12px',
                          background: 'white',
                          border: '1px solid #e0e6ed',
                          borderRadius: '6px',
                          marginBottom: '6px'
                        }}
                      >
                        <span style={{ fontSize: '14px', color: '#2c3e50' }}>{email}</span>
                        <button
                          type="button"
                          onClick={() => handleRemoveRecipient(email)}
                          style={{
                            padding: '4px 8px',
                            background: '#fee2e2',
                            color: '#dc2626',
                            border: '1px solid #ef4444',
                            borderRadius: '4px',
                            fontSize: '12px',
                            cursor: 'pointer'
                          }}
                        >
                          Remove
                        </button>
                      </div>
                    ))}
                  </div>
                )}

                {/* Add Recipient Input */}
                <div style={{ display: 'flex', gap: '8px' }}>
                  <input
                    type="email"
                    className="input"
                    value={newRecipientEmail}
                    onChange={(e) => setNewRecipientEmail(e.target.value)}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter') {
                        e.preventDefault();
                        handleAddRecipient();
                      }
                    }}
                    placeholder="email@example.com"
                    style={{ flex: 1 }}
                  />
                  <button
                    type="button"
                    onClick={handleAddRecipient}
                    className="btn btn-secondary"
                    style={{ whiteSpace: 'nowrap' }}
                  >
                    + Add Email
                  </button>
                </div>
                <div style={{ fontSize: '12px', color: '#64748b', marginTop: '6px' }}>
                  {recipients.length === 0 ? 'Add at least one email recipient' : `${recipients.length} recipient${recipients.length !== 1 ? 's' : ''} added`}
                </div>
              </div>

              {/* Alert Types */}
              <div style={{ marginBottom: '20px' }}>
                <label style={{ display: 'block', marginBottom: '8px', fontWeight: '600' }}>
                  Alert Types <span style={{ color: '#ef4444' }}>*</span>
                </label>
                <div style={{
                  padding: '16px',
                  background: '#f8fafc',
                  border: '2px solid #e0e6ed',
                  borderRadius: '8px'
                }}>
                  {ALERT_TYPES.map(alertType => (
                    <label
                      key={alertType.id}
                      style={{
                        display: 'flex',
                        alignItems: 'flex-start',
                        gap: '12px',
                        padding: '12px',
                        marginBottom: '8px',
                        background: selectedAlerts.has(alertType.id) ? '#e0f2fe' : 'white',
                        border: `2px solid ${selectedAlerts.has(alertType.id) ? '#0ea5e9' : '#e0e6ed'}`,
                        borderRadius: '6px',
                        cursor: 'pointer',
                        transition: 'all 0.2s'
                      }}
                      onMouseEnter={(e) => {
                        if (!selectedAlerts.has(alertType.id)) {
                          e.currentTarget.style.background = '#f8fafc';
                        }
                      }}
                      onMouseLeave={(e) => {
                        if (!selectedAlerts.has(alertType.id)) {
                          e.currentTarget.style.background = 'white';
                        }
                      }}
                    >
                      <input
                        type="checkbox"
                        checked={selectedAlerts.has(alertType.id)}
                        onChange={() => toggleAlertType(alertType.id)}
                        style={{ marginTop: '2px' }}
                      />
                      <div style={{ flex: 1 }}>
                        <div style={{ fontSize: '14px', fontWeight: '600', color: '#2c3e50', marginBottom: '2px' }}>
                          {alertType.icon} {alertType.label}
                        </div>
                        <div style={{ fontSize: '12px', color: '#64748b' }}>
                          {alertType.description}
                        </div>
                      </div>
                    </label>
                  ))}
                </div>
                <div style={{ fontSize: '12px', color: '#64748b', marginTop: '6px' }}>
                  {selectedAlerts.size === 0 ? 'Select at least one alert type' : `${selectedAlerts.size} alert type${selectedAlerts.size !== 1 ? 's' : ''} selected`}
                </div>
              </div>

              {/* Buttons */}
              <div style={{ display: 'flex', gap: '10px', marginTop: '24px' }}>
                <button
                  type="button"
                  onClick={handleCloseModal}
                  className="btn btn-secondary"
                  style={{ flex: 1 }}
                  disabled={saving}
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="btn btn-primary"
                  style={{ flex: 1 }}
                  disabled={saving || recipients.length === 0 || selectedAlerts.size === 0 || !groupName.trim()}
                >
                  {saving ? 'Saving...' : (modalMode === 'edit' ? 'Update Alert Group' : 'Create Alert Group')}
                </button>
              </div>
            </form>
          </div>
        </div>,
        document.body
      )}
    </div>
  );
}
